// Export builders for the Hash Verifier tool: a plain-text log and a printable
// PDF, both meant to be attached to a case file as chain-of-custody
// documentation. Kept separate from the page component so the text format is
// directly testable.

import { ALGORITHMS } from './hashFile';

function algoLabel(id) {
  return ALGORITHMS.find(a => a.id === id)?.label || id.toUpperCase();
}

export function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * A plain-text hash log covering every finished job — one block per file,
 * every computed algorithm, and the verification result if the user checked
 * one against an expected value.
 *
 * @param {Array} jobs  finished jobs: { name, size, hashes, expectedHash, verifiedAgainst, computedAt }
 */
export function buildHashLogText(jobs) {
  const lines = [
    'FILE HASH LOG',
    `Generated ${new Date().toLocaleString()} by LE Tech Tools — computed locally in the browser.`,
    '='.repeat(72),
    '',
  ];

  for (const job of jobs) {
    lines.push(job.name, `  Size: ${fmtBytes(job.size)}`);
    for (const [algo, hash] of Object.entries(job.hashes || {})) {
      lines.push(`  ${algoLabel(algo)}: ${hash}`);
    }
    if (job.expectedHash) {
      lines.push(`  Expected hash checked: ${job.expectedHash}`);
      lines.push(`  Result: ${job.verifiedAgainst ? 'MATCH' : 'MISMATCH'}`);
    }
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * A pdfmake docDefinition for a printable hash report: one section per file,
 * a table of algorithm → hash, and the verification result when present.
 */
export function buildHashReportDoc(jobs) {
  const content = [
    { text: 'File Hash Report', fontSize: 20, bold: true, margin: [0, 0, 0, 2] },
    {
      text: `Generated ${new Date().toLocaleString()} — computed locally in the browser, never uploaded.`,
      fontSize: 9, color: '#64748b', margin: [0, 0, 0, 18],
    },
  ];

  for (const job of jobs) {
    const rows = Object.entries(job.hashes || {}).map(([algo, hash]) => ([
      { text: algoLabel(algo), bold: true, fontSize: 9 },
      { text: hash, fontSize: 8.5, font: 'Roboto' },
    ]));

    content.push(
      { text: job.name, fontSize: 13, bold: true, margin: [0, 10, 0, 2] },
      { text: fmtBytes(job.size), fontSize: 9, color: '#64748b', margin: [0, 0, 0, 6] },
      {
        table: { widths: [70, '*'], body: rows },
        layout: {
          hLineWidth: () => 0.5, vLineWidth: () => 0,
          hLineColor: () => '#e2e8f0',
          paddingTop: () => 4, paddingBottom: () => 4,
        },
      },
    );

    if (job.expectedHash) {
      const ok = job.verifiedAgainst;
      content.push({
        margin: [0, 6, 0, 0],
        table: {
          widths: ['*'],
          body: [[{
            stack: [
              { text: ok ? '✓ MATCHES expected hash' : '✗ DOES NOT MATCH expected hash', bold: true, color: ok ? '#15803d' : '#b91c1c', fontSize: 10 },
              { text: job.expectedHash, fontSize: 8.5, color: '#475569', margin: [0, 2, 0, 0] },
            ],
            fillColor: ok ? '#f0fdf4' : '#fef2f2',
            margin: [10, 7, 10, 7],
          }]],
        },
        layout: 'noBorders',
      });
    }
  }

  return {
    content,
    defaultStyle: { fontSize: 10, color: '#0f172a' },
    pageMargins: [40, 40, 40, 40],
  };
}
